import React, { useCallback, useReducer, useState } from "react";
import { Button, Loading, Modal } from "@alm/flow-ds/components";
import QueryByForm from "./QueryByForm";
import SearchResults from "./SearchResults";
import { SearchConfig } from "lib/api/types";
import { useTranslation } from "react-i18next";
import finderReducer, {
	addSearchResults,
	addSelected,
	clearSelected,
	defaultState,
	setSearchResults,
} from "./finderReducer";
import _ from "lodash";
import api from "lib/api";
import jsonpointer from "lib/jsonpointer/jsonpointer";
import axios, { AxiosError } from "axios";
import { getErrorMessage } from "lib/api/errors";

export type AddSelectedCallback = (selectedIds: {
	values: string[];
	label: string[];
}) => void;

type Props = SearchConfig & {
	selectLimit?: number;
	onAddSelected: AddSelectedCallback;
	onCloseClick: () => void;
	title: string;
};

function Finder(props: Props) {
	const { t } = useTranslation();

	// Used to show a loader when making API requests
	const [loading, setLoading] = useState(false);
	const [loadingPage, setLoadingPage] = useState(false);

	// Toggle between the search form and results
	const [formVisible, setFormVisible] = useState(true);

	// Error message for 500s, things we can't recover from
	const [error, setError] = useState<undefined | string>();

	// Setup our reducer to manage state
	const [
		{
			activeSearchFields,
			searchFields,
			query,
			resultsHasMore,
			results,
			selectedRecords,
			cursorId,
			querySummary,
		},
		dispatch,
	] = useReducer(
		finderReducer,
		defaultState({
			searchFields: props.view
				? props.view.elements?.reduce(
						(fields, e) =>
							Object.assign(fields, {
								[e.name]: e,
							}),
						{}
				  )
				: [],
			activeSearchFields: props.view
				? [props.view.elements[0]?.name]
				: [],
		})
	);

	/**
	 * Handle form submissions - when users hit enter on their keyboard
	 */
	const onSearchSubmit = useCallback(async () => {
		// Hide the form
		setFormVisible(false);

		// Show the loader
		setLoading(true);

		// Clear any selected records from previous searches
		dispatch(clearSelected());

		// Create our query payload
		const payload = _.cloneDeep(props.payload);
		payload.filter.AND = [...payload.filter.AND, ...query];
		payload.select = props.columns.map(c => jsonpointer.pointerToId(c.id));

		try {
			const resp = await api.cursors.create(payload);
			dispatch(setSearchResults(resp));
		} catch (err) {
			const e = err as AxiosError;
			setError(
				getErrorMessage(e) || t("elements.finder.genericSearchErrorMsg")
			);
		} finally {
			setLoading(false);
		}
	}, [props.columns, props.payload, query, t]);

	/**
	 * Search results pagination handler
	 */
	const handleLoadMore = useCallback(async () => {
		setLoadingPage(true);

		try {
			if (!cursorId) {
				throw new Error("Could not page, no cursorId found.");
			}
			const resp = await api.cursors.nextPage(cursorId);
			dispatch(addSearchResults(resp));
		} catch (error) {
			console.error(error);
			if (error && axios.isAxiosError(error)) setError(error.message);
		} finally {
			setLoadingPage(false);
		}
	}, [cursorId]);

	/**
	 * Add an id to the selected records array
	 */
	const handleAddSelected = useCallback(
		async (id: string[]) => dispatch(addSelected(id)),
		[]
	);

	// Handles the actual adding of items by converting array
	// positions to the record id
	const onAddSelected = useCallback(() => {
		if (results) {
			const selectedData: { values: string[]; label: string[] } = {
				values: [],
				label: [],
			};
			selectedRecords.forEach(id => {
				const index = parseInt(id);
				const selectedId = results[index].id;
				const labelFieldName = props.label.slice(1);
				const label = results[index].data[labelFieldName];
				selectedData.values.push(selectedId);
				selectedData.label.push(label as string);
			});
			props.onAddSelected(selectedData);
			props.onCloseClick();
		}
	}, [props, results, selectedRecords]);

	/**
	 * Toggle visibility between the search form and search results
	 */
	const toggleSearchVisibility = useCallback(
		() => setFormVisible(!formVisible),
		[formVisible]
	);

	return (
		<Modal
			className={`finder__modal modal--fill-h results-visible--${!formVisible}`}
			modalSize="xl"
			isOpen={true}
			title={props.title}
			footer={
				formVisible ? (
					<Button
						type="submit"
						className="btn-primary finder__search-btn"
						label={t("elements.finder.search-btn-label")}
						onClick={onSearchSubmit}
						disabled={loading ? true : false}
					/>
				) : (
					<div className="finder__search-results-footer">
						{/* close the modal */}
						<Button
							className="btn-default finder__search-results-footer--cancel"
							label={t("common.labels.cancel")}
							onClick={props.onCloseClick}
							disabled={loading || loadingPage ? true : false}
						/>
						{/* see which items are selected and add it  */}
						<Button
							className="btn-primary finder__search-btn finder__search-results-footer--add"
							label={t("elements.finder.addSelected")}
							onClick={onAddSelected}
							disabled={
								loading ||
								loadingPage ||
								selectedRecords.length === 0
									? true
									: false
							}
						/>
					</div>
				)
			}
			close={props.onCloseClick}
			closeAriaLabel={t("main.icons.closeModal")}>
			<>
				{error ? (
					<div
						className="alert show block-alert block-alert--error"
						role="alert">
						<div className="alert__message">
							<div className="alert__desc">
								<p className="mb-0">{error}</p>
							</div>
						</div>
					</div>
				) : (
					<>
						{loading && (
							<Loading className="elem__loader--lg mt-5 mb-5" />
						)}

						{results !== undefined && !loading && (
							<div className="finder__search-toolbar">
								<div className="finder__search-summary d-none d-md-flex">
									<span className="finder__search-summary-title me-2">
										{t(
											"elements.finder.search-criteria-title"
										)}
									</span>
									{activeSearchFields.map((f, i) => (
										<span
											className="finder__search-summary-field me-2"
											key={`summ-${i}`}>
											<span className="finder__search-summary-field-name me-1">
												{searchFields[f]?.label}:
											</span>
											<span className="finder__search-summary-field-value">
												<small>{querySummary[i]}</small>
											</span>
										</span>
									))}
								</div>
								<div className="finder__toggle-form-visibility ms-auto ms-md-2">
									<Button
										className="btn-link"
										label={
											formVisible
												? t(
														"elements.finder.show-results-btn-label"
												  )
												: t(
														"elements.finder.modify-criteria-btn-label"
												  )
										}
										onClick={toggleSearchVisibility}
									/>
								</div>
							</div>
						)}

						<div
							style={{
								display:
									formVisible && !loading ? "block" : "none",
							}}
							className="finder__search-form">
							<QueryByForm
								activeSearchFields={activeSearchFields}
								dispatch={dispatch}
								searchFields={searchFields}
							/>
						</div>

						{!formVisible && !loading && (
							<SearchResults
								limit={props.selectLimit}
								hasMore={resultsHasMore}
								results={results}
								onLoadMore={handleLoadMore}
								columns={props.columns}
								selected={selectedRecords}
								loadingPage={loadingPage}
								addSelected={handleAddSelected}
							/>
						)}
					</>
				)}
			</>
		</Modal>
	);
}

export default Finder;
